import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, CheckCircle, XCircle, RefreshCw, Loader2, AlertCircle, Package, DollarSign, Calendar, MessageSquare } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';

interface TipoSuscripcion {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  mensajes_max: number;
  precio: number;
  duracion_dias: number;
  caracteristicas: Array<{nombre: string; valor: string}>;
  estado: 'activo' | 'inactivo';
  created_at: string;
  updated_at: string;
}

export default function TiposSuscripcion() {
  const { user } = useAuthStore();
  const [tiposSuscripcion, setTiposSuscripcion] = useState<TipoSuscripcion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTipo, setEditingTipo] = useState<TipoSuscripcion | null>(null);
  const [newTipo, setNewTipo] = useState<Omit<TipoSuscripcion, 'id' | 'created_at' | 'updated_at'>>({
    codigo: '',
    nombre: '',
    descripcion: '',
    mensajes_max: 1000,
    precio: 0,
    duracion_dias: 30,
    caracteristicas: [],
    estado: 'activo'
  });
  const [newCaracteristica, setNewCaracteristica] = useState({ nombre: '', valor: '' });

  useEffect(() => {
    if (user) {
      fetchTiposSuscripcion();
    }
  }, [user]);

  const fetchTiposSuscripcion = async () => {
    try {
      setIsLoading(true);
      setError('');

      const { data, error } = await supabase
        .from('tipos_suscripcion')
        .select('*')
        .order('precio');

      if (error) throw error;
      setTiposSuscripcion(data || []);
    } catch (error: any) {
      console.error('Error fetching subscription types:', error);
      setError('Error al cargar los tipos de suscripción');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingTipo) {
        const { error } = await supabase
          .from('tipos_suscripcion')
          .update({
            codigo: newTipo.codigo,
            nombre: newTipo.nombre,
            descripcion: newTipo.descripcion,
            mensajes_max: newTipo.mensajes_max,
            precio: newTipo.precio,
            duracion_dias: newTipo.duracion_dias,
            caracteristicas: newTipo.caracteristicas,
            estado: newTipo.estado
          })
          .eq('id', editingTipo.id);

        if (error) throw error;
        setSuccess('Tipo de suscripción actualizado correctamente');
      } else {
        const { error } = await supabase
          .from('tipos_suscripcion')
          .insert([newTipo]);

        if (error) throw error;
        setSuccess('Tipo de suscripción creado correctamente');
      }

      setShowModal(false);
      setEditingTipo(null);
      resetForm();
      fetchTiposSuscripcion();
    } catch (error: any) {
      console.error('Error saving subscription type:', error);
      setError(error.message);
    }
  };

  const handleEdit = (tipo: TipoSuscripcion) => {
    setEditingTipo(tipo);
    setNewTipo({
      codigo: tipo.codigo,
      nombre: tipo.nombre,
      descripcion: tipo.descripcion || '',
      mensajes_max: tipo.mensajes_max,
      precio: tipo.precio,
      duracion_dias: tipo.duracion_dias,
      caracteristicas: tipo.caracteristicas || [],
      estado: tipo.estado
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este tipo de suscripción? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      // Check if any empresas are using this subscription type
      const { count, error: countError } = await supabase
        .from('empresas')
        .select('*', { count: 'exact', head: true })
        .eq('tipo_suscripcion_id', id);

      if (countError) throw countError;

      if (count && count > 0) {
        throw new Error(`No se puede eliminar este tipo de suscripción porque está siendo utilizado por ${count} empresas.`);
      }

      const { error } = await supabase
        .from('tipos_suscripcion')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuccess('Tipo de suscripción eliminado correctamente');
      fetchTiposSuscripcion();
    } catch (error: any) {
      console.error('Error deleting subscription type:', error);
      setError(error.message);
    }
  };

  const handleUpdateStatus = async (id: string, newStatus: 'activo' | 'inactivo') => {
    try {
      const { error } = await supabase
        .from('tipos_suscripcion')
        .update({ estado: newStatus })
        .eq('id', id);

      if (error) throw error;
      setSuccess(`Tipo de suscripción ${newStatus === 'activo' ? 'activado' : 'desactivado'} correctamente`);
      fetchTiposSuscripcion();
    } catch (error: any) {
      console.error('Error updating subscription type status:', error);
      setError(error.message);
    }
  };

  const handleAddCaracteristica = () => {
    if (!newCaracteristica.nombre || !newCaracteristica.valor) return;
    
    setNewTipo({
      ...newTipo,
      caracteristicas: [...newTipo.caracteristicas, newCaracteristica]
    });
    
    setNewCaracteristica({ nombre: '', valor: '' });
  };

  const handleRemoveCaracteristica = (index: number) => {
    const updatedCaracteristicas = [...newTipo.caracteristicas];
    updatedCaracteristicas.splice(index, 1);
    
    setNewTipo({
      ...newTipo,
      caracteristicas: updatedCaracteristicas
    });
  };

  const resetForm = () => {
    setNewTipo({
      codigo: '',
      nombre: '',
      descripcion: '',
      mensajes_max: 1000,
      precio: 0,
      duracion_dias: 30,
      caracteristicas: [],
      estado: 'activo'
    });
    setNewCaracteristica({ nombre: '', valor: '' });
  };

  const filteredTipos = tiposSuscripcion.filter(tipo =>
    tipo.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tipo.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tipo.descripcion?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Tipos de Suscripción</h1>
        <button 
          onClick={() => {
            setEditingTipo(null);
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Añadir Tipo de Suscripción
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Buscar tipos de suscripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
            <button
              onClick={fetchTiposSuscripcion}
              className="ml-2 p-2 text-gray-600 hover:bg-gray-100 rounded-full"
              title="Actualizar lista"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-semibold text-gray-600">Nombre</th>
                  <th className="pb-3 font-semibold text-gray-600">Código</th>
                  <th className="pb-3 font-semibold text-gray-600">Mensajes</th>
                  <th className="pb-3 font-semibold text-gray-600">Precio</th>
                  <th className="pb-3 font-semibold text-gray-600">Duración</th>
                  <th className="pb-3 font-semibold text-gray-600">Estado</th>
                  <th className="pb-3 font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredTipos.map((tipo) => (
                  <tr key={tipo.id} className="border-b">
                    <td className="py-3">
                      <div className="flex items-center">
                        <Package className="w-5 h-5 text-blue-500 mr-2" />
                        <div>
                          <div className="font-medium">{tipo.nombre}</div>
                          <div className="text-sm text-gray-500">{tipo.descripcion}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm">{tipo.codigo}</code>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center">
                        <MessageSquare className="w-4 h-4 text-gray-500 mr-2" />
                        <span>{tipo.mensajes_max === 0 ? 'Ilimitados' : tipo.mensajes_max.toLocaleString()}</span>
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center">
                        <DollarSign className="w-4 h-4 text-gray-500 mr-2" />
                        <span>{tipo.precio.toFixed(2)} €</span>
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-500 mr-2" />
                        <span>{tipo.duracion_dias} días</span>
                      </div>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        tipo.estado === 'activo' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {tipo.estado}
                      </span>
                    </td>
                    <td className="py-3">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleUpdateStatus(tipo.id, tipo.estado === 'activo' ? 'inactivo' : 'activo')}
                          className={`p-1 rounded-full ${
                            tipo.estado === 'activo' ? 'text-red-500 hover:bg-red-100' : 'text-green-500 hover:bg-green-100'
                          }`}
                          title={tipo.estado === 'activo' ? 'Desactivar' : 'Activar'}
                        >
                          {tipo.estado === 'activo' ? <XCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                        </button>
                        <button 
                          onClick={() => handleEdit(tipo)}
                          className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                          title="Editar tipo de suscripción"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={() => handleDelete(tipo.id)}
                          className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                          title="Eliminar tipo de suscripción"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredTipos.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No se encontraron tipos de suscripción</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal para añadir/editar tipo de suscripción */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingTipo ? 'Editar Tipo de Suscripción' : 'Nuevo Tipo de Suscripción'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código
                  </label>
                  <input
                    type="text"
                    required
                    value={newTipo.codigo}
                    onChange={(e) => setNewTipo({...newTipo, codigo: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="basic"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Identificador único para el tipo de suscripción
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre
                  </label>
                  <input
                    type="text"
                    required
                    value={newTipo.nombre}
                    onChange={(e) => setNewTipo({...newTipo, nombre: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Básico"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  rows={2}
                  value={newTipo.descripcion || ''}
                  onChange={(e) => setNewTipo({...newTipo, descripcion: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Descripción del tipo de suscripción"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mensajes Máximos
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={newTipo.mensajes_max}
                    onChange={(e) => setNewTipo({...newTipo, mensajes_max: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    0 para mensajes ilimitados
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio (€)
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={newTipo.precio}
                    onChange={(e) => setNewTipo({...newTipo, precio: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duración (días)
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={newTipo.duracion_dias}
                    onChange={(e) => setNewTipo({...newTipo, duracion_dias: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <select
                  value={newTipo.estado}
                  onChange={(e) => setNewTipo({...newTipo, estado: e.target.value as 'activo' | 'inactivo'})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Características
                </label>
                
                <div className="mb-2 flex space-x-2">
                  <input
                    type="text"
                    placeholder="Nombre"
                    value={newCaracteristica.nombre}
                    onChange={(e) => setNewCaracteristica({...newCaracteristica, nombre: e.target.value})}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <input
                    type="text"
                    placeholder="Valor"
                    value={newCaracteristica.valor}
                    onChange={(e) => setNewCaracteristica({...newCaracteristica, valor: e.target.value})}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={handleAddCaracteristica}
                    className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    Añadir
                  </button>
                </div>
                
                {newTipo.caracteristicas.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {newTipo.caracteristicas.map((caracteristica, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 whitespace-nowrap">{caracteristica.nombre}</td>
                            <td className="px-4 py-2 whitespace-nowrap">{caracteristica.valor}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-right">
                              <button
                                type="button"
                                onClick={() => handleRemoveCaracteristica(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                    No hay características definidas
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingTipo(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingTipo ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}